import { downloadCustomerDocument, DownloadCustomerDocumentApiParameter } from "@iewccouk/Services/CommonService";
import { CustomState } from "@iewccouk/Store/Reducers";
import throwErrorIfTesting from "@insite/client-framework/Common/ThrowErrorIfTesting";
import {
    createHandlerChainRunner,
    Handler,
    HasOnError,
    HasOnSuccess,
    markSkipOnCompleteIfOnErrorIsSet,
    markSkipOnCompleteIfOnSuccessIsSet,
} from "@insite/client-framework/HandlerCreator";
import logger from "@insite/client-framework/Logger";

type Parameter = DownloadCustomerDocumentApiParameter & HasOnSuccess<any> & HasOnError<unknown>;

type Props = {
    apiParameter: DownloadCustomerDocumentApiParameter;
    apiResult: any;
    error?: unknown;
};

type HandlerType = Handler<Parameter, Props>;

export const DispatchLoadingStart: HandlerType = props => {
    props.dispatch({
        type: "Custom/Downloads/SetDownloadLoading",
        isLoading: true,
        isSuccess: true,
    } as any);
};

export const PopulateApiParameter: HandlerType = props => {
    throwErrorIfTesting();
    props.apiParameter = {
        documentId: props.parameter.documentId,
        type: props.parameter.type,
    };
};

export const SendDataToApi: HandlerType = async props => {
    const downloadableState = (props.getState() as CustomState).customDownloads.downloadables;

    if (
        downloadableState[props.parameter.type] &&
        downloadableState[props.parameter.type][props.parameter.documentId]
    ) {
        props.apiResult = downloadableState[props.parameter.type][props.parameter.documentId];
    } else {
        try {
            const result = await downloadCustomerDocument(props.apiParameter);
            if (result.successful) {
                props.apiResult = result.result;
            } else {
                logger.warn(`Failed to get downloadable data: ${result.errorMessage}`);
                props.error = result.errorMessage;
            }
        } catch (error) {
            logger.warn(`Failed to get downloadable data: ${error}`);
            props.error = error;
        }
    }
};

export const DispatchDownloadablesData: HandlerType = props => {
    const {
        parameter: { type: documentType, documentId },
        apiResult: value,
    } = props;

    props.dispatch({
        type: "Custom/Downloads/SetDownloadablesByType",
        documentType,
        documentId,
        value,
    } as any);
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    if (!props.error) {
        markSkipOnCompleteIfOnSuccessIsSet(props);
        props.parameter.onSuccess?.(props.apiResult);
    }
};

export const ExecuteOnErrorCallback: HandlerType = props => {
    if (props.error) {
        markSkipOnCompleteIfOnErrorIsSet(props);
        props.parameter.onError?.(props.error);
    }
};

export const DispatchLoadingEnd: HandlerType = props => {
    props.dispatch({
        type: "Custom/Downloads/SetDownloadLoading",
        isLoading: false,
        isSuccess: !props.error,
    } as any);
};

export const chain = [
    DispatchLoadingStart,
    PopulateApiParameter,
    SendDataToApi,
    DispatchDownloadablesData,
    DispatchLoadingEnd,
    ExecuteOnSuccessCallback,
    ExecuteOnErrorCallback,
];

const downloadDocumentsByType = createHandlerChainRunner(chain, "DownloadDocumentsByType");
export default downloadDocumentsByType;
